/**
 * Non-interface helper methods for LoaderPreProcess
 * Contains private helpers and methods not part of ILoader interface
 *
 * These methods are attached to LoaderPreProcess.prototype in the main file
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { JsonArray, JsonObject } from '../../app/json/jsonValue.js';
import { ResourceStatistics } from '../../model/index.js';
import { PreProcessTemplateEntry, PreprocessedTemplate } from '../model/index.js';
import { preprocessTemplate } from './generatePreProcessMetadata.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { IConfig } from '../../config/iConfig.js';
import type { PreparedContent } from '../../config/preparedContent.js';
import type { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';

/**
 * Accessor methods to be attached to LoaderPreProcess prototype
 */
export const accessorMethods = {
    // #region Configuration (Non-Interface)

    /**
     * Skip patterns for Dynamic View handlers (comma-delimited or "*").
     * Mirrors C# `LoaderPreProcess_Accessors.cs : public string? SkipDynamicView`.
     * Attached to LoaderPreProcess.prototype via Object.defineProperties; `this` is bound to the instance at call time.
     */
    get skipDynamicView(): string | null { return (this as unknown as LoaderPreProcess)._skipDynamicView; },
    set skipDynamicView(value: string | null) { (this as unknown as LoaderPreProcess)._skipDynamicView = value; },

    /**
     * Skip patterns for Dynamic Data handlers (comma-delimited or "*").
     * Mirrors C# `LoaderPreProcess_Accessors.cs : public string? SkipDynamicData`.
     */
    get skipDynamicData(): string | null { return (this as unknown as LoaderPreProcess)._skipDynamicData; },
    set skipDynamicData(value: string | null) { (this as unknown as LoaderPreProcess)._skipDynamicData = value; },

    /**
     * Gets SearchAppSites for the specified AppSite
     * Delegates to IConfig.getSearchAppSitesForAppSite
     */
    getSearchAppSitesForAppSite(this: LoaderPreProcess, appSite: string): string {
        return this._config!.getSearchAppSitesForAppSite(appSite);
    },

    /**
     * Returns the loaded, fully-resolved templates for an AppSite (replacement mappings already created).
     * Used by the OCI artifact generator to serialize the server-computed PreprocessedTemplate set so the
     * client can rehydrate it — the artifact must capture POST-Load state, not a per-template parse whose
     * replacementMappings are still empty. Mirrors C# LoaderPreProcess_Accessors.GetLoadedTemplates.
     */
    getLoadedTemplates(this: LoaderPreProcess, appSite: string): Map<string, PreProcessTemplateEntry> {
        const cached = this._store.tryGetTemplates(appSite);
        return cached ? cached : new Map<string, PreProcessTemplateEntry>();
    },

    // #endregion

    // #region Statistics (Non-Interface)

    /**
     * Gets resource statistics from loader's cached data
     */
    getResourceStatistics(this: LoaderPreProcess, appSite: string): ResourceStatistics {
        const cachedStats = this._store.tryGetResourceStats(appSite);
        if (cachedStats !== undefined) {
            return cachedStats;
        }
        const stats = this._computeResourceStatistics(appSite);
        this._store.setResourceStats(appSite, stats);
        return stats;
    },

    // #endregion

    // #region Statistics Helpers (Private)

    /**
     * Computes resource statistics from loader's cached data
     */
    _computeResourceStatistics(this: LoaderPreProcess, appSite: string): ResourceStatistics {
        const stats = new ResourceStatistics();
        const cached = this._store.tryGetTemplates(appSite);
        if (cached) {
            for (const [, value] of cached.entries()) {
                if (value.template && value.template.originalContent &&
                    value.template.originalContent !== this._config!.defaultReplaceHtml) {
                    stats.templateCount++;
                }
                if (value.json) {
                    stats.jsonFileCount++;
                    (this.constructor as typeof LoaderPreProcess)._countJsonKeysAndArrays(value.json, stats);
                }
            }
        }
        const dynamicDataConfig = this.getDynamicDataConfig(appSite);
        if (dynamicDataConfig) {
            stats.dynamicDataHandlerCount = (this.constructor as typeof LoaderPreProcess)._countHandlersInConfig(dynamicDataConfig);
        }
        const dynamicViewConfig = this.getDynamicViewConfig(appSite);
        if (dynamicViewConfig) {
            stats.dynamicViewHandlerCount = (this.constructor as typeof LoaderPreProcess)._countHandlersInConfig(dynamicViewConfig);
        }
        return stats;
    },

    // #endregion

    // #region Loader State Diagnostic

    getLoaderStateJson(this: LoaderPreProcess, appSite: string): string {
        const sb: string[] = [];
        sb.push('{');

        // Template keys
        sb.push('"TemplateKeys":[');
        const cached = this._store.tryGetTemplates(appSite);
        if (cached) {
            const sortedKeys = Array.from(cached.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            for (let i = 0; i < sortedKeys.length; i++) {
                if (i > 0) sb.push(',');
                sb.push('"'); sb.push(sortedKeys[i]); sb.push('"');
            }
        }
        sb.push('],');

        sb.push('"GlobalDataStore":{');
        const gdKeys = Object.keys(this._globalDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gdKeys[i]); sb.push('":');
            const gd = this._globalDataStore[gdKeys[i]];
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataStore":{');
        const gvdKeys = Object.keys(this._globalViewDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdKeys[i]); sb.push('":');
            const gvd = this._globalViewDataStore[gvdKeys[i]];
            sb.push(gvd ? JsonUtility.serializeObject(gvd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataComponentStore":{');
        const gvdcKeys = Object.keys(this._globalViewDataComponentStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdcKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdcKeys[i]); sb.push('":');
            const gvdc = this._globalViewDataComponentStore[gvdcKeys[i]];
            sb.push(gvdc ? JsonUtility.serializeObject(gvdc) : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataConfigStore":{');
        const ddKeys = Object.keys(this._dynamicDataConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddKeys[i]); sb.push('":');
            const dd = this._dynamicDataConfigStore[ddKeys[i]];
            sb.push(dd ? dd.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicViewConfigStore":{');
        const dvKeys = Object.keys(this._dynamicViewConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvKeys[i]); sb.push('":');
            const dv = this._dynamicViewConfigStore[dvKeys[i]];
            sb.push(dv ? dv.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicActionConfigStore":{');
        const daKeys = Object.keys(this._dynamicActionConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < daKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(daKeys[i]); sb.push('":');
            const da = this._dynamicActionConfigStore[daKeys[i]];
            sb.push(da ? da.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataParamsKeys":[');
        const ddpKeys = Array.from(this._dynamicDataParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicViewParamsKeys":[');
        const dvpKeys = Array.from(this._dynamicViewParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicActionParamsKeys":[');
        const dapKeys = Array.from(this._dynamicActionParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dapKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dapKeys[i]); sb.push('"');
        }
        sb.push(']');

        sb.push('}');
        return sb.join('');
    },

    getLoaderPerSiteStoresJson(this: LoaderPreProcess, appSite: string): string {
        const sb: string[] = [];
        this._serializePerSiteStores(sb, appSite);
        const json = sb.join('');
        const prefix = ',"PerSiteStores":';
        return json.startsWith(prefix) ? json.substring(prefix.length) : '{}';
    }

    // #endregion
};

/**
 * Static methods to be attached to LoaderPreProcess class
 */
export const accessorStaticMethods = {
    // #region Loading Templates (Private)

    /**
     * Loads templates from a single AppSite using IConfig content retrieval
     * Optimization: If config provides preprocessed templates (client mode), use them directly
     * Otherwise, load raw HTML and preprocess (server mode)
     */
    loadTemplatesFromSingleAppSite(config: IConfig, prepared: PreparedContent, appSite: string): Map<string, PreProcessTemplateEntry> {
        const result = new Map<string, PreProcessTemplateEntry>();
        const fileNames = config.getAllContentNames(appSite);
        if (fileNames.length === 0) {
            Logger.warn(`No file names found for AppSite: ${appSite}`, 'LoaderPreProcess');
            return result;
        }

        Logger.debug(`Loading templates for AppSite: ${appSite} (${fileNames.length} files)`, 'LoaderPreProcess');

        for (const fileName of fileNames) {
            // Skip dynamic/global config entries — they are loaded separately into dedicated stores
            if (CommonUtil.isDynamicOrGlobalConfigName(fileName)) continue;

            const key = `${appSite.toLowerCase()}_${fileName.toLowerCase()}`;

            const json = prepared.getDataJson(appSite, fileName);

            let preprocessed: PreprocessedTemplate | null = null;
            const htmlContent = prepared.getHtmlContent(appSite, fileName) || '';

            // Rebuild preprocess metadata from HTML in both server and client modes.
            // The API payload intentionally stores a reduced nested slot graph, which is
            // sufficient for transport but not for preserving exact nested ops shape.
            if (htmlContent) {
                preprocessed = preprocessTemplate(htmlContent, appSite, key, fileName);
            } else if (json) {
                // JSON-only template
                preprocessed = new PreprocessedTemplate();
                preprocessed.originalContent = '';
                preprocessed.actualTemplateName = fileName;
            }

            if (preprocessed || json) {
                result.set(key, new PreProcessTemplateEntry(preprocessed!, json));
            }
        }

        Logger.debug(`Loaded ${result.size} templates for ${appSite}`, 'LoaderPreProcess');
        return result;
    },

    BuildAppSitesToLoad(appSite: string, searchAppSites: string): string[] {
        const appSitesToLoad: string[] = [];
        const seen = new Set<string>();

        const addAppSite = (site: string | null | undefined): void => {
            if (!site) {
                return;
            }

            const trimmedSite = site.trim();
            if (!trimmedSite) {
                return;
            }

            const lowerSite = trimmedSite.toLowerCase();
            if (seen.has(lowerSite)) {
                return;
            }

            seen.add(lowerSite);
            appSitesToLoad.push(trimmedSite);
        };

        addAppSite(appSite);
        for (const searchAppSite of searchAppSites.split(',')) {
            addAppSite(searchAppSite);
        }

        return appSitesToLoad;
    },

    // #endregion

    // #region Skip Handler Helper

    /**
     * Checks if a handler should be skipped based on skip patterns
     */
    _shouldSkipHandler(handlerType: string, skipPatterns: string | null): boolean {
        if (!handlerType) {
            return true;
        }

        if (!skipPatterns || skipPatterns.trim() === '') {
            return false;
        }

        const normalizedPatterns = skipPatterns.trim();
        if (normalizedPatterns === '*') {
            return true;
        }

        const lowerHandlerType = handlerType.toLowerCase();
        for (const pattern of normalizedPatterns.split(',')) {
            const trimmedPattern = pattern.trim().toLowerCase();
            if (!trimmedPattern) {
                continue;
            }

            if (trimmedPattern === '*' || trimmedPattern === lowerHandlerType) {
                return true;
            }
        }

        return false;
    },

    // #endregion

    // #region Statistics Helpers (Private)

    /**
     * Recursively counts keys and arrays in a JsonObject
     */
    _countJsonKeysAndArrays(json: JsonObject | null, stats: ResourceStatistics): void {
        if (!json) return;
        for (const key of json.keys()) {
            stats.jsonKeyCount++;
            const value = json.get(key);
            if (value instanceof JsonObject) {
                this._countJsonKeysAndArrays(value, stats);
            } else if (value instanceof JsonArray) {
                stats.jsonArrayCount++;
                for (let i = 0; i < value.length; i++) {
                    const item = value.get(i);
                    if (item instanceof JsonObject) {
                        this._countJsonKeysAndArrays(item, stats);
                    }
                }
            }
        }
    },

    /**
     * Counts total handlers in a DynamicData/DynamicView config
     * Supports DynamicConfigMap and JsonObject
     */
    _countHandlersInConfig(config: JsonObject | DynamicConfigMap): number {
        let count = 0;
        const keys = config.keys();
        for (const componentKey of keys) {
            const handlersArray = config.get(componentKey);
            if (handlersArray) {
                if (handlersArray instanceof JsonArray) count += handlersArray.length;
                else if (Array.isArray(handlersArray)) count += handlersArray.length;
            }
        }
        return count;
    }

    // #endregion
};
